import type { Role } from '@auth/features/auth';
import type { LucideIcon } from 'lucide-react';
import { hasRole, role, useAuthData } from '@auth/features/auth';
import {
  OrganizationInitDialog,
  useShouldShowInit,
} from '@auth/features/organization-init';
import { useNavigate } from '@tanstack/react-router';
import {
  Activity,
  Bot,
  Braces,
  ClipboardList,
  Code2,
  Coins,
  FileSpreadsheet,
  Hotel,
  KeyRound,
  Languages,
  ListChecks,
  ListTodo,
  MapPinned,
  MessageSquareText,
  Newspaper,
  Receipt,
  Rocket,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Target,
  Users as UsersIcon,
  Video,
  Workflow,
} from 'lucide-react';
import { useState } from 'react';
import { Button, Text, Tooltip } from '@/shared/ui';
import css from './admin-menu.module.scss';

// `roles` names who may reach the item, defaulting to admin-only. It exists for
// the handful of surfaces the cross-org `system` super-user must also reach —
// that role holds no resource permissions, so every other admin page would 403
// for it and must stay hidden.
type AdminItem = {
  icon: LucideIcon;
  label: string;
  path: string;
  roles?: Role[];
};
type AdminSection = { id: string; title: string; items: AdminItem[] };

const DEFAULT_ITEM_ROLES: Role[] = [role.admin];

const ADMIN_SECTIONS: AdminSection[] = [
  {
    id: 'user-management',
    title: 'User Management',
    items: [
      { icon: UsersIcon, label: 'Accounts', path: '/user-settings/accounts' },
      { icon: Target, label: 'Sales Goals', path: '/management/sales-goals' },
    ],
  },
  {
    id: 'system-management',
    title: 'Configuration',
    items: [
      {
        icon: SlidersHorizontal,
        label: 'System Configuration',
        path: '/system-configuration',
      },
      {
        icon: ClipboardList,
        label: 'Partner Configuration',
        path: '/partner-configuration',
      },
      // XT-773 — super-user only, and deliberately NOT `role.admin`: designing
      // the status process is Lumera's tool, not per-org administration.
      {
        icon: Workflow,
        label: 'Status Flow',
        path: '/status-flow/itinerary',
        roles: [role.system],
      },
      {
        icon: MessageSquareText,
        label: 'WhatsApp',
        path: '/messaging/templates',
      },
      {
        icon: ListTodo,
        label: 'Task Management',
        path: '/tasks-management/tasks',
      },
      {
        icon: Video,
        label: 'Regional Videos',
        path: '/system-configuration/video-suggestions',
      },
    ],
  },
  {
    id: 'pricing-configuration',
    title: 'Pricing Configuration',
    items: [
      {
        icon: Coins,
        label: 'Pricing Type',
        path: '/pricing-configuration/pricing-type',
        // v2 parity: the legacy avatar menu rendered the whole "Pricing
        // Settings" group for [ADMIN, FINANCE, SUPER_FINANCE] (XT-620).
        roles: [role.admin, role.finance, role.superFinance],
      },
      {
        icon: Receipt,
        label: 'Payment Logs',
        path: '/management/payment-logs',
        // Finance-team surface, not an admin tool. Legacy v2 rendered it for
        // [ADMIN, FINANCE, SUPER_FINANCE]; the v3 port dropped the finance
        // roles and locked the team out (XT-620). Keep in sync with the
        // `/management/payment-logs` rule in route-permissions.ts.
        roles: [role.admin, role.finance, role.superFinance],
      },
      {
        icon: Bot,
        label: 'Recommendation',
        path: '/management/recommendations',
      },
    ],
  },
  {
    id: 'tools',
    title: 'Tools',
    items: [
      {
        icon: Code2,
        label: 'Embed Generator',
        path: '/management/embed-generator',
      },
      {
        icon: Activity,
        label: 'API Monitoring',
        path: '/api-monitoring/monitor',
      },
      {
        icon: Languages,
        label: 'Auto Translate Catalog',
        path: '/auto-translate-catalog',
      },
      {
        icon: FileSpreadsheet,
        label: 'Exports',
        path: '/export-configuration/products',
      },
      { icon: MapPinned, label: 'Custom Regions', path: '/custom-regions' },
      {
        icon: ShieldCheck,
        label: 'Inconsistency Resolver',
        path: '/inconsistency-resolver',
      },
      {
        icon: ListChecks,
        label: 'Bulk Status Change',
        path: '/management/bulk-status-change',
        roles: [role.admin, role.system],
      },
    ],
  },
  {
    id: 'integrations',
    title: 'Integrations',
    items: [
      { icon: Newspaper, label: 'CMS', path: '/cms/landing-pages' },
      { icon: Braces, label: 'OptiGest', path: '/management/optigest' },
      {
        icon: KeyRound,
        label: 'Product API',
        path: '/management/product-api',
      },
      {
        icon: Hotel,
        label: 'HyperGuest',
        path: '/management/hyperguest/mapper',
      },
    ],
  },
];

export const AdminMenu = () => {
  const { data: authData } = useAuthData();
  const callerRoles = authData?.user?.roles ?? [];
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [initOpen, setInitOpen] = useState(false);
  const { shouldShow: showInit, status: initStatus } = useShouldShowInit();

  const visibleSections = ADMIN_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) =>
      (item.roles ?? DEFAULT_ITEM_ROLES).some((required) =>
        hasRole(callerRoles, required)
      )
    ),
  })).filter((section) => section.items.length > 0);

  // Hide the trigger only when nothing inside is reachable. Previously this
  // early-returned on `isAdminOrSystem`, which hid the whole menu from the
  // finance roles even after their items opted in via `AdminItem.roles`
  // (XT-620).
  if (visibleSections.length === 0 && !showInit) return null;

  return (
    <>
      {open && (
        <button
          aria-label="Close admin menu"
          className={css.backdrop}
          onClick={() => setOpen(false)}
          type="button"
        />
      )}
      <div className={css.root}>
        <Tooltip content="Admin" position="bottom">
          <Button
            aria-label="Admin menu"
            className={css.trigger}
            icon={<Settings style={{ height: 16, width: 16 }} />}
            onClick={() => setOpen((prev) => !prev)}
            size="icon"
            variant="ghost"
          />
        </Tooltip>

        {open && (
          <div className={css.dropdown}>
            {showInit && (
              <div className={css.section}>
                <Text
                  as="span"
                  className={css.sectionLabel}
                  size="xs"
                  weight="medium"
                >
                  Setup
                </Text>
                <button
                  className={css.item}
                  onClick={() => {
                    setOpen(false);
                    setInitOpen(true);
                  }}
                  type="button"
                >
                  <Rocket className={css.itemIcon} />
                  <Text as="span" size="sm">
                    Initialize Organization
                  </Text>
                </button>
              </div>
            )}
            {visibleSections.map((section) => (
              <div className={css.section} key={section.id}>
                <Text
                  as="span"
                  className={css.sectionLabel}
                  size="xs"
                  weight="medium"
                >
                  {section.title}
                </Text>
                {section.items.map(({ icon: Icon, label, path }) => (
                  <button
                    className={css.item}
                    key={path}
                    onClick={() => {
                      setOpen(false);
                      // biome-ignore lint/suspicious/noExplicitAny: cross-domain route reference
                      navigate({ to: path as any });
                    }}
                    type="button"
                  >
                    <Icon className={css.itemIcon} />
                    <Text as="span" size="sm">
                      {label}
                    </Text>
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      <OrganizationInitDialog
        onClose={() => setInitOpen(false)}
        open={initOpen}
        status={initStatus ?? null}
      />
    </>
  );
};
